import BugReportIcon from '@mui/icons-material/BugReport';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import PhonePausedIcon from '@mui/icons-material/PhonePaused';
import RingVolumeIcon from '@mui/icons-material/RingVolume';
import Button, { ButtonProps } from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import { grey } from '@mui/material/colors';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { keyframes, styled } from '@mui/material/styles';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { MUIStyledCommonProps, Theme } from '@mui/system';
import AgentStatusBarBase, { StatusColor } from 'components/AgentStatusBarBase';
import React, { useEffect, useState } from 'react';

import { RoutingProfile } from '~pages/CampaignManagement/domain';
import {
  AgentAvailableStates,
  AgentState,
  AgentStateType,
  ConnectContactType,
  ConnectVoiceContact,
  ContactStateType,
} from '~providers/ConnectProvider/domain';

import PhoneWarningIcon from './PhoneWarningIcon';
import RoutingProfileSelector from './RoutingProfileSelector';

enum AdditionalStatusBadgeType {
  Hold = 'hold',
  Unknown = 'unknown',
}

interface StatusBadge {
  backgroundColor: StatusColor;
  statusName: string;
  statusIcon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
}

export interface StatisticItem {
  icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
  text: string;
}

type Props = {
  statusList: string[];
  routingProfiles: RoutingProfile[];
  agentStatusType: AgentStateType;
  agentStatus: AgentState;
  agentStatusTimestamp: string;
  currentVoiceContact?: ConnectVoiceContact;
  isVoiceViewActive?: boolean;
  isMessagingViewActive?: boolean;
  isAsyncPendingStateChange?: boolean;
  isAsyncMissedConversation?: boolean;
  attemptHasPreviousInteraction?: boolean;
  unseenMessages?: boolean;
  statisticItems?: StatisticItem[];
  isStateChangeDisabled?: boolean;
  setAgentState: (statusName: string) => Promise<void>;
  setRoutingProfile: ((routingProfileID: string, routingProfileName: string) => Promise<void>) | undefined;
  downloadSessionLogs: () => void;
  onVoiceButtonClick?: () => void;
  onMessagingButtonClick?: () => void;
};

const statusBadgeData: { [key: string]: StatusBadge } = {
  [AgentStateType.Offline]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: '',
    statusIcon: PhoneDisabledIcon,
  },
  [AgentStateType.NotRoutable]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: '',
    statusIcon: PhoneDisabledIcon,
  },
  [AgentStateType.Routable]: {
    backgroundColor: StatusColor.green,
    statusName: '',
    statusIcon: PhoneIcon,
  },
  [AgentAvailableStates.PendingBusy]: {
    backgroundColor: StatusColor.green,
    statusName: 'Ringing',
    statusIcon: RingVolumeIcon,
  },
  // Ringing state for queue callback contacts only (voice contacts skip this state and ring within connecting instead)
  [ContactStateType.Incoming]: {
    backgroundColor: StatusColor.green,
    statusName: 'Incoming Callback',
    statusIcon: RingVolumeIcon,
  },
  // Queue callback only transient state between the agent accepting the callback and connect dialling the customer
  // back within the connecting state
  [ContactStateType.Pending]: {
    backgroundColor: StatusColor.green,
    statusName: 'Callback Accepted',
    statusIcon: PhoneIcon,
  },
  [ContactStateType.Connecting]: {
    backgroundColor: StatusColor.green,
    statusName: 'Ringing',
    statusIcon: RingVolumeIcon,
  },
  [ContactStateType.Connected]: {
    backgroundColor: StatusColor.blue,
    statusName: 'Connected',
    statusIcon: PhoneInTalkIcon,
  },
  [ContactStateType.Ended]: {
    backgroundColor: StatusColor.grey,
    statusName: 'After Call Work',
    statusIcon: EditIcon,
  },
  // Inbound missed call state
  [ContactStateType.Missed]: {
    backgroundColor: StatusColor.red,
    statusName: 'Missed Call',
    statusIcon: PhoneMissedIcon,
  },
  // Outbound call no answer state
  [ContactStateType.Error]: {
    backgroundColor: StatusColor.red,
    statusName: 'No Answer',
    statusIcon: PhoneMissedIcon,
  },
  // Agent declined an incoming contact (i.e. an incoming queue callback)
  [ContactStateType.Rejected]: {
    backgroundColor: StatusColor.red,
    statusName: 'Rejected',
    statusIcon: PhoneMissedIcon,
  },
  [ContactStateType.Paused]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Paused',
    statusIcon: PhonePausedIcon,
  },
  [AdditionalStatusBadgeType.Hold]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Hold',
    statusIcon: PhonePausedIcon,
  },
  [AdditionalStatusBadgeType.Unknown]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Unknown',
    statusIcon: BugReportIcon,
  },
};

type ClearButtonProps = MUIStyledCommonProps<Theme> &
  ButtonProps & {
    isActive?: boolean;
    isPulse?: boolean;
  };

const heartBeat = keyframes({
  '0%': {
    boxShadow: '0 0 0 0 rgba(255,255,255, 1)',
  },
  '70%': {
    boxShadow: '0 0 0 10px rgba(204,169,44, 0)',
  },
  '100%': {
    boxShadow: '0 0 0 0 rgba(204,169,44, 0)',
  },
});

const ClearButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isPulse',
})<ClearButtonProps>(({ theme, isActive, isPulse }) => ({
  'minWidth': 100,
  'fontWeight': 400,
  'lineHeight': 1.334,
  'textTransform': 'none',
  'backgroundColor': isActive ? 'rgba(255, 255, 255, .40)' : 'rgba(255, 255, 255, .10)',
  'color': '#ffffff',
  ':active': {
    backgroundColor: isActive ? 'rgba(255, 255, 255, .40)' : 'initial',
  },
  ':hover': {
    backgroundColor: isActive ? 'rgba(255, 255, 255, .40)' : 'rgba(255, 255, 255, .20)',
  },
  '&.MuiButtonGroup-grouped:not(:last-of-type)': {
    borderColor: 'rgba(255, 255, 255, .65)',
  },
  'animation': isPulse ? `${heartBeat} 1.5s infinite` : undefined,
}));

const PreviewStatusBar = ({
  statusList,
  routingProfiles,
  agentStatusType,
  agentStatus,
  agentStatusTimestamp,
  currentVoiceContact,
  isVoiceViewActive,
  isMessagingViewActive,
  isAsyncPendingStateChange,
  isAsyncMissedConversation,
  attemptHasPreviousInteraction,
  unseenMessages,
  statisticItems,
  isStateChangeDisabled,
  setAgentState,
  setRoutingProfile,
  downloadSessionLogs,
  onVoiceButtonClick,
  onMessagingButtonClick,
}: Props) => {
  const [holdTimestamp, setHoldTimestamp] = useState<string>(new Date().toISOString());
  const isOnHold = currentVoiceContact?.isOnHold;
  let statusData;
  const voiceButtonPulse = attemptHasPreviousInteraction;
  const messagingButtonPulse = isAsyncMissedConversation || unseenMessages;
  let VoiceButtonIcon = PhoneIcon;
  let MessagingButtonIcon = ChatBubbleIcon;

  if (attemptHasPreviousInteraction) {
    VoiceButtonIcon = PhoneWarningIcon;
  }

  // If async has a missed conversation it takes priority over any new messages notifications that come through
  if (isAsyncMissedConversation) {
    MessagingButtonIcon = FeedbackIcon;
  } else if (unseenMessages) {
    MessagingButtonIcon = MarkChatUnreadIcon;
  }

  // Handles hold timer
  useEffect(() => {
    if (isOnHold) {
      setHoldTimestamp(new Date().toISOString());
    }
  }, [isOnHold]);

  // Most important first
  if (currentVoiceContact && currentVoiceContact.isOnHold) {
    statusData = statusBadgeData[AdditionalStatusBadgeType.Hold];
  } else if (currentVoiceContact !== undefined) {
    statusData = statusBadgeData[currentVoiceContact.statusType];

    // The connecting state is shared between contact types (ringing for standard voice calls), so for accepted queue
    // callbacks we rename it here to make it clear to the agent that the customer is being dialled back.
    if (
      currentVoiceContact.contactType === ConnectContactType.QueueCallback &&
      currentVoiceContact.statusType === ContactStateType.Connecting
    ) {
      statusData = { ...statusData, statusName: 'Calling Customer Back' };
    }
    // We want to catch specific agent states then contact states
  } else if (
    agentStatusType === AgentStateType.Offline ||
    agentStatusType === AgentStateType.Routable ||
    agentStatusType === AgentStateType.NotRoutable
  ) {
    statusData = statusBadgeData[agentStatusType];
    // Update status name with dynamic custom status name's
    // We add a toString call on agentStatus here as we have custom states also coming through at run time and we are
    // unable to define them in the type interface
    statusData.statusName = agentStatus.toString();
    // We want to handle a specific agent available state
  } else if (agentStatusType === AgentStateType.System && agentStatus === AgentAvailableStates.PendingBusy) {
    statusData = statusBadgeData[AgentAvailableStates.PendingBusy];
  }

  // If not of the above conditions match OR one of them result in an undefined case
  if (!statusData) {
    statusData = statusBadgeData[AdditionalStatusBadgeType.Unknown];
  }

  const { backgroundColor, statusName, statusIcon: StatusIcon } = statusData;

  return (
    <>
      <AgentStatusBarBase
        status={isAsyncPendingStateChange ? `Pending: ${statusName}` : statusName}
        statusTimestamp={isOnHold ? holdTimestamp : agentStatusTimestamp}
        statusIcon={StatusIcon}
        statusColor={backgroundColor}
        isStateChangeDisabled={isStateChangeDisabled}
        statusList={statusList}
        setAgentState={setAgentState}
        actionsSection={
          <>
            {(onVoiceButtonClick || onMessagingButtonClick) && (
              <ButtonGroup sx={{ marginRight: 1, height: 30 }} variant='contained' disableElevation>
                {onVoiceButtonClick && (
                  <ClearButton
                    isActive={isVoiceViewActive}
                    isPulse={voiceButtonPulse}
                    variant='contained'
                    disableElevation
                    onClick={onVoiceButtonClick}
                    color='primary'
                    startIcon={<VoiceButtonIcon fontSize='small' />}>
                    Voice
                  </ClearButton>
                )}

                {onMessagingButtonClick && (
                  <ClearButton
                    isActive={isMessagingViewActive}
                    isPulse={messagingButtonPulse}
                    variant='contained'
                    disableElevation
                    onClick={onMessagingButtonClick}
                    color='primary'
                    startIcon={<MessagingButtonIcon fontSize='small' />}>
                    Messaging
                  </ClearButton>
                )}
              </ButtonGroup>
            )}

            {setRoutingProfile && (
              <RoutingProfileSelector
                onChange={setRoutingProfile}
                items={routingProfiles.map((item) => ({ label: item.name, value: item.id }))}
                disabled={Boolean(currentVoiceContact || isStateChangeDisabled)}
              />
            )}
          </>
        }
        subActionsSection={
          <Button
            sx={{
              'fontSize': 12,
              'textTransform': 'none',
              'lineHeight': 1.43,
              'padding': '2px 16px',
              'color': 'rgba(0, 0, 0, 0.87)',
              'backgroundColor': '#e0e0e0',
              ':hover': {
                backgroundColor: '#d5d5d5',
              },
            }}
            variant='contained'
            disableElevation
            startIcon={<CloudDownloadIcon fontSize='small' />}
            onClick={downloadSessionLogs}>
            Session Logs
          </Button>
        }
        statsSection={statisticItems?.map(({ icon: Icon, text }: StatisticItem, index: number) => (
          <Chip
            key={index}
            sx={{ backgroundColor: 'transparent', padding: 0, fontWeight: 500, fontSize: 12, color: grey[900] }}
            size='small'
            icon={<Icon />}
            label={text}
          />
        ))}
      />
    </>
  );
};

export default PreviewStatusBar;
